/**
 * Poner un tema en su forma buena, y sembrar lo que le falte.
 *
 * No es sólo validar: `normalizeProjectTheme` DERIVA. De los roles de un tema
 * saca los componentes de color, tipografía, botón, tarjeta e icono que el editor
 * enseña como globales, y vuelve a sincronizar los roles con ellos. Un tema al que
 * no se le pasa por aquí no tiene botones que editar.
 *
 * Por eso vive con la derivación y no en la aplicación del editor: el panel
 * necesita exactamente lo mismo para poder enseñar y editar la marca, y una
 * segunda copia habría empezado a divergir en el primer retoque.
 */
import type { StyleProperties } from '@webbuilder/style-engine'
import { outlinedCardStyles } from './outlinedCard'
import { DEFAULT_PAGE_THEME } from './defaults'
import { contrastRatio, distinctPrimaryColor } from './palettes'
import { readableVar } from './presets'
import { normalizeSiteMotion } from './motion'
import {
  DEFAULT_BRAND_LOGO,
  type BrandLogo,
  type GlobalButtonComponent,
  type GlobalCardComponent,
  type GlobalColorComponent,
  type GlobalIconComponent,
  type GlobalSectionComponent,
  type GlobalTypographyComponent,
  type PageTheme,
  type ProjectTheme,
  type ThemeRole,
  type ThemeRoleElementProps,
} from './types'

export const BUILT_IN_COLOR_COMPONENTS: Omit<GlobalColorComponent, 'value'>[] = [
  { id: 'primary', nameKey: 'panel.primary', cssVar: '--wb-primary', builtIn: true },
  { id: 'backgroundSecondary', nameKey: 'color.alternate', cssVar: '--wb-backgroundSecondary', builtIn: true },
  { id: 'surface', nameKey: 'panel.surface', cssVar: '--wb-surface', builtIn: true },
  { id: 'background', nameKey: 'panel.background', cssVar: '--wb-background', builtIn: true },
  /* «Muted» era el nombre de la variable, no de lo que pinta: el texto
     secundario de los bloques —fechas, etiquetas, notas—. */
  { id: 'muted', nameKey: 'color.mutedText', cssVar: '--wb-muted', builtIn: true },
  { id: 'text', nameKey: 'el.texto', cssVar: '--wb-text', builtIn: true },
  { id: 'accent', nameKey: 'brand.accent', cssVar: '--wb-accent', builtIn: true },
  { id: 'card', nameKey: 'brand.card', cssVar: '--wb-card', builtIn: true },
  { id: 'border', nameKey: 'brand.borders', cssVar: '--wb-border', builtIn: true },
]

const BUILT_IN_TYPOGRAPHY_COMPONENTS: {
  id: string
  name?: string
  nameKey?: string
  tag: string
  role: ThemeRole
}[] = [
  { id: 'h1', name: 'H1', tag: 'h1', role: 'heading1' },
  { id: 'h2', name: 'H2', tag: 'h2', role: 'heading2' },
  { id: 'h3', name: 'H3', tag: 'h3', role: 'heading3' },
  { id: 'h4', name: 'H4', tag: 'h4', role: 'heading4' },
  { id: 'h5', name: 'H5', tag: 'h5', role: 'heading5' },
  { id: 'h6', name: 'H6', tag: 'h6', role: 'heading6' },
  { id: 'p', nameKey: 'brand.paragraph', tag: 'p', role: 'paragraph' },
  { id: 'small', nameKey: 'brand.eyebrow', tag: 'small', role: 'eyebrow' },
  { id: 'small-text', nameKey: 'bp.smallText', tag: 'small', role: 'smallText' },
]

/**
 * Los estilos de texto que trae todo proyecto. Quien tenga que saber si uno es
 * «de fábrica» —el revisor de plantillas, por ejemplo— lo lee de aquí: con una
 * lista copiada a mano, al añadir «Texto pequeño» las plantillas que lo usaban
 * se marcaban como «estilo que sólo tiene esta plantilla».
 */
export const BUILT_IN_TYPOGRAPHY_IDS: readonly string[] = BUILT_IN_TYPOGRAPHY_COMPONENTS.map((item) => item.id)

const BUILT_IN_BUTTON_COMPONENTS: {
  id: string
  name?: string
  nameKey?: string
  role: ThemeRole
}[] = [
  { id: 'button-primary', nameKey: 'brand.buttonPrimary', role: 'buttonPrimary' },
  { id: 'button-secondary', nameKey: 'brand.buttonSecondary', role: 'buttonSecondary' },
  { id: 'button-link', name: 'Link', role: 'link' },
]

/* Los lados del borde DESPUÉS del grosor, estilo y color generales: en el CSS
   gana lo último, y con `borderWidth: 0px` detrás de `borderBottom` una línea
   de un solo lado no se veía nunca. */
const CARD_CONTAINER_KEYS = [
  'background', 'backgroundColor', 'backgroundImage',
  'border', 'borderWidth', 'borderStyle', 'borderColor',
  'borderTop', 'borderRight', 'borderBottom', 'borderLeft', 'borderRadius',
  'boxShadow', 'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
] as const satisfies readonly (keyof StyleProperties)[]
export const CARD_CONTAINER_KEY_SET = new Set<string>(CARD_CONTAINER_KEYS)

/* Fondo, espaciado y borde. La sombra no: es del contenido que lleva dentro.
   El borde sí, para la línea que separa una franja de la siguiente —la de
   debajo de la barra de navegación, la de encima del footer—. */
const SECTION_CONTAINER_KEYS = [
  'background', 'backgroundColor', 'backgroundImage',
  'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'border', 'borderWidth', 'borderStyle', 'borderColor', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft', 'outline', 'outlineOffset',
  // Las esquinas: las de la barra cuando flota, o una sección tipo tarjeta.
  'borderRadius',
] as const satisfies readonly (keyof StyleProperties)[]
export const SECTION_CONTAINER_KEY_SET = new Set<string>(SECTION_CONTAINER_KEYS)

/** El aire a los lados de toda sección con estilo: 24px, y 20px en móvil. */
const SECTION_SIDES = { paddingLeft: '24px', paddingRight: '24px' } as const
const SECTION_SIDES_MOBILE = { paddingLeft: '20px', paddingRight: '20px' } as const
/**
 * El aire de arriba y abajo de los estilos de sección de fábrica.
 *
 * Un estilo de sección manda sobre el relleno de la sección: si el estilo no
 * lo pone, no lo pone nadie. «Default» y «Secondary» sólo traían los lados, así
 * que con el tema por defecto una sección ligada a ellos salía pegada arriba y
 * abajo. Un estilo de diseño o un ajuste en Style › Secciones lo reemplaza.
 */
const SECTION_VERTICAL = { paddingTop: '96px', paddingBottom: '96px' } as const
const SECTION_VERTICAL_TABLET = { paddingTop: '72px', paddingBottom: '72px' } as const
const SECTION_VERTICAL_MOBILE = { paddingTop: '56px', paddingBottom: '56px' } as const

export const ICON_TOKEN_STYLE_KEYS = [
  'color', 'background', 'backgroundColor',
  // Internal sizing behavior shared with the right inspector. These are stored
  // by the editor when iconSize changes, but are not exposed to AI as free styles.
  'width', 'height',
  'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'border', 'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
  'boxShadow',
] as const satisfies readonly (keyof StyleProperties)[]

function cardContainerStyles(styles: StyleProperties): StyleProperties {
  const next: StyleProperties = {}
  for (const key of CARD_CONTAINER_KEYS) {
    const value = styles[key]
    if (value !== undefined) next[key] = value
  }
  return next
}

/**
 * El ancho del contenido de un estilo de sección, si es uno válido: `'100%'` o
 * una medida en px entre 320 y 2560. Cualquier otra cosa se descarta y la
 * sección vuelve a decidir por su cuenta.
 */
function normalizeContentWidth(value: unknown): string | undefined {
  if (value === '100%') return '100%'
  const px = typeof value === 'string' ? /^(\d+(?:\.\d+)?)px$/.exec(value.trim()) : null
  if (!px) return undefined
  const n = Math.round(Number(px[1]))
  return n >= 320 && n <= 2560 ? `${n}px` : undefined
}

function normalizeContentWidthBreakpoints(value: unknown): { tablet?: string; mobile?: string } | undefined {
  if (!value || typeof value !== 'object') return undefined
  const input = value as { tablet?: unknown; mobile?: unknown }
  const out: { tablet?: string; mobile?: string } = {}
  const tablet = normalizeContentWidth(input.tablet)
  const mobile = normalizeContentWidth(input.mobile)
  if (tablet) out.tablet = tablet
  if (mobile) out.mobile = mobile
  return out.tablet || out.mobile ? out : undefined
}

function sectionContainerStyles(styles: StyleProperties): StyleProperties {
  const next: StyleProperties = {}
  for (const key of SECTION_CONTAINER_KEYS) {
    const value = styles[key]
    if (value !== undefined) next[key] = value
  }
  return next
}

const LEGACY_BASE_CARD_DEFAULTS = {
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
} as const

function migrateBaseCardDefaults(styles: StyleProperties): StyleProperties {
  const wasLegacyBase = styles.borderRadius === LEGACY_BASE_CARD_DEFAULTS.borderRadius
    && styles.boxShadow === LEGACY_BASE_CARD_DEFAULTS.boxShadow
  const transparentCard = 'color-mix(in srgb, var(--wb-card) 0%, transparent)'
  const wasTransparentDefault = (
    (styles.background === transparentCard || styles.backgroundColor === transparentCard)
    && styles.borderRadius === '24px'
    && styles.boxShadow === 'none'
  )
  if (!wasLegacyBase && !wasTransparentDefault) return styles
  return {
    ...styles,
    background: 'var(--wb-card)',
    backgroundColor: 'var(--wb-card)',
    borderRadius: '24px',
    boxShadow: 'none',
  }
}

/** El color que pinta un valor de tema: una variable `--wb-*` o un hex; si no, null. */
function themeColorValue(value: unknown, colors: PageTheme['colors']): string | null {
  if (typeof value !== 'string') return null
  const token = /^\s*var\(--wb-(\w+)\)\s*$/.exec(value)
  if (token) return (colors as Record<string, string | undefined>)[token[1]!] ?? null
  return /^\s*#[0-9a-f]{3,8}\s*$/i.test(value) ? value.trim() : null
}

/* Las letras que el editor pone solo en un icono de fábrica. Un color elegido a
   mano que no esté aquí —un hex cualquiera— no se toca. */
const AUTO_ICON_INKS = new Set([
  'var(--wb-primary)', 'var(--wb-accent)', 'var(--wb-muted)', 'var(--wb-text)', 'var(--wb-background)', '#FFFFFF', '#0B0B0B',
])
const ICON_PREFERRED_INK: Record<string, string> = { 'icon-primary': 'primary', 'icon-plain': 'primary', 'icon-secondary': 'muted', 'icon-accent': 'background' }
/** Lo mínimo para un símbolo gráfico (WCAG 1.4.11). */
const ICON_MIN_CONTRAST = 3

/**
 * Un icono de fábrica siempre se ve sobre su caja.
 *
 * El «de acento» es el acento sobre el primario, y el primario NACE del acento
 * (`distinctPrimaryColor` lo aclara apenas cuando son el mismo): en la mitad de
 * las paletas el símbolo y la caja eran casi el mismo color —1,1:1—. Y el
 * principal, el primario sobre el fondo alterno, desaparecía con un primario
 * claro (un amarillo sobre gris perla).
 *
 * Sólo cuando no se lee: si el color que lleva llega a 3:1 se queda. Si no, el
 * de su papel si ése sí llega, y si tampoco, la letra que se lee sobre la caja
 * —la misma regla que la letra del botón—. Va en la normalización, así que
 * sirve al cambiar de paleta o de estilo y también a un proyecto ya guardado.
 */
function readableIconInk(id: string, styles: StyleProperties, colors: PageTheme['colors']) {
  const ink = styles.color
  if (typeof ink !== 'string' || !AUTO_ICON_INKS.has(ink)) return
  const fillValue = styles.backgroundColor ?? styles.background
  const fill = fillValue === undefined || fillValue === 'transparent' || fillValue === 'none'
    ? colors.background
    : themeColorValue(fillValue, colors)
  /* El de acento lleva el color del fondo siempre que se lea, como la letra de
     un botón: con un acento claro pasa a la tinta del texto, y al cambiar a un
     acento oscuro vuelve. Sin esto se quedaba con la del texto para siempre. */
  if (id === 'icon-accent' && (ink === 'var(--wb-background)' || ink === 'var(--wb-text)') && fill && colors.background) {
    styles.color = contrastRatio(colors.background, fill) >= ICON_MIN_CONTRAST ? 'var(--wb-background)' : readableVar(fill, colors)
    return
  }
  const inkColor = themeColorValue(ink, colors)
  if (!fill || !inkColor || contrastRatio(inkColor, fill) >= ICON_MIN_CONTRAST) return
  const preferred = ICON_PREFERRED_INK[id]
  const preferredColor = preferred ? (colors as Record<string, string | undefined>)[preferred] : undefined
  styles.color = preferredColor && contrastRatio(preferredColor, fill) >= ICON_MIN_CONTRAST
    ? `var(--wb-${preferred})`
    : readableVar(fill, colors)
}

function iconTokenStyles(styles: StyleProperties): StyleProperties {
  const next: StyleProperties = {}
  for (const key of ICON_TOKEN_STYLE_KEYS) {
    const value = styles[key]
    if (value !== undefined) next[key] = value
  }
  return next
}

function componentsFromTheme(theme: PageTheme): Pick<ProjectTheme, 'colorComponents' | 'typographyComponents' | 'buttonComponents' | 'cardComponents' | 'sectionComponents' | 'iconComponents'> {
  return {
    colorComponents: BUILT_IN_COLOR_COMPONENTS.map((token) => ({
      ...token,
      value: theme.colors[token.id as keyof PageTheme['colors']] ?? DEFAULT_PAGE_THEME.colors[token.id as keyof PageTheme['colors']] ?? '#000000',
    })),
    typographyComponents: BUILT_IN_TYPOGRAPHY_COMPONENTS.map((item) => ({
      id: item.id,
      ...(item.name ? { name: item.name } : {}),
      ...(item.nameKey ? { nameKey: item.nameKey } : {}),
      tag: item.tag,
      role: item.role,
      styles: { ...theme.roles[item.role] },
      breakpoints: {
        ...(theme.roleBreakpoints?.tablet?.[item.role] ? { tablet: { ...theme.roleBreakpoints.tablet[item.role] } } : {}),
        ...(theme.roleBreakpoints?.mobile?.[item.role] ? { mobile: { ...theme.roleBreakpoints.mobile[item.role] } } : {}),
      },
      builtIn: true,
    })),
    buttonComponents: BUILT_IN_BUTTON_COMPONENTS.map((item) => ({
      id: item.id,
      ...(item.name ? { name: item.name } : {}),
      ...(item.nameKey ? { nameKey: item.nameKey } : {}),
      role: item.role,
      styles: { ...theme.roles[item.role] },
      breakpoints: {
        ...(theme.roleBreakpoints?.tablet?.[item.role] ? { tablet: { ...theme.roleBreakpoints.tablet[item.role] } } : {}),
        ...(theme.roleBreakpoints?.mobile?.[item.role] ? { mobile: { ...theme.roleBreakpoints.mobile[item.role] } } : {}),
      },
      builtIn: true,
    })),
    cardComponents: [
      {
        id: 'card-default',
        nameKey: 'brand.cardBase',
        role: 'card',
        styles: cardContainerStyles(theme.roles.card),
        builtIn: true,
      },
      {
        id: 'card-soft',
        nameKey: 'brand.cardSoft',
        styles: {
          background: 'var(--wb-card)',
          backgroundColor: 'var(--wb-card)',
          borderColor: 'color-mix(in srgb, var(--wb-border) 35%, transparent)',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderRadius: '24px',
          boxShadow: '0 6px 16px rgba(15,23,42,0.05)',
          padding: '42px',
          paddingTop: '42px',
          paddingRight: '42px',
          paddingBottom: '42px',
          paddingLeft: '42px',
        },
        builtIn: true,
      },
      {
        id: 'card-elevated',
        nameKey: 'brand.cardColor',
        styles: {
          background: 'var(--wb-accent)',
          backgroundColor: 'var(--wb-accent)',
          borderColor: 'var(--wb-accent)',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderRadius: '24px',
          boxShadow: 'none',
          padding: '42px',
          paddingTop: '42px',
          paddingRight: '42px',
          paddingBottom: '42px',
          paddingLeft: '42px',
        },
        builtIn: true,
      },
    ],
    sectionComponents: [
      /* Todas con aire a los lados: el contenido va en una columna de ancho
         limitado, pero en una pantalla más estrecha que ella tocaba el borde.
         Un estilo de sección manda sobre el relleno propio de la sección, así
         que si el estilo no lo pone, no lo pone nadie. */
      {
        id: 'section-default',
        nameKey: 'brand.sectionBase',
        role: 'section',
        styles: { ...SECTION_VERTICAL, ...sectionContainerStyles(theme.roles.section), ...SECTION_SIDES },
        breakpoints: { tablet: { ...SECTION_VERTICAL_TABLET }, mobile: { ...SECTION_VERTICAL_MOBILE, ...SECTION_SIDES_MOBILE } },
        /* Nace limitado, como el pie. Sólo en proyectos que aún no tienen el
           estilo guardado: ver `NEW_ONLY_CONTENT_WIDTH` más abajo. */
        contentWidth: '1280px',
        builtIn: true,
      },
      {
        id: 'section-secondary',
        nameKey: 'brand.sectionSecondary',
        styles: {
          background: 'var(--wb-backgroundSecondary)',
          backgroundColor: 'var(--wb-backgroundSecondary)',
          ...SECTION_VERTICAL,
          ...SECTION_SIDES,
        },
        breakpoints: { tablet: { ...SECTION_VERTICAL_TABLET }, mobile: { ...SECTION_VERTICAL_MOBILE, ...SECTION_SIDES_MOBILE } },
        contentWidth: '1280px',
        builtIn: true,
      },
      /* Sobre el acento de la marca: la franja que destaca —una llamada a la
         acción, una cifra, un anuncio—. La letra se vuelve clara sola si el
         color es oscuro (ver `applyAutoContrastBackground`). */
      {
        id: 'section-accent',
        nameKey: 'brand.sectionAccent',
        styles: {
          background: 'var(--wb-accent)',
          backgroundColor: 'var(--wb-accent)',
          ...SECTION_VERTICAL,
          ...SECTION_SIDES,
        },
        breakpoints: { tablet: { ...SECTION_VERTICAL_TABLET }, mobile: { ...SECTION_VERTICAL_MOBILE, ...SECTION_SIDES_MOBILE } },
        contentWidth: '1280px',
        builtIn: true,
      },
      /* La barra de navegación y el footer, con estilo de sección propio: son
         franjas de la página como las demás, pero con su aire —la barra, poco;
         el footer, sobre la tinta—. Los traen puestos al insertarse, y cambiar
         uno en Style › Secciones cambia la barra o el footer de todo el sitio. */
      {
        id: 'section-nav',
        nameKey: 'brand.sectionNav',
        /* Separada de la página por una línea fina en el color de borde, como
           las barras de producto de hoy: el mismo fondo que la página, sin
           una franja de otro color que la haga pesada. */
        styles: {
          background: 'var(--wb-background)',
          backgroundColor: 'var(--wb-background)',
          paddingTop: '14px',
          paddingBottom: '14px',
          ...SECTION_SIDES,
          borderWidth: '0px',
          borderStyle: 'solid',
          borderColor: 'var(--wb-border)',
          borderBottom: '1px solid var(--wb-border)',
        },
        breakpoints: { mobile: { paddingTop: '10px', paddingBottom: '10px', ...SECTION_SIDES_MOBILE } },
        contentWidth: '1280px',
        builtIn: true,
      },
      {
        id: 'section-footer',
        nameKey: 'brand.sectionFooter',
        styles: {
          background: 'var(--wb-text)',
          backgroundColor: 'var(--wb-text)',
          paddingTop: '72px',
          paddingBottom: '40px',
          ...SECTION_SIDES,
        },
        breakpoints: { mobile: { paddingTop: '48px', paddingBottom: '32px', ...SECTION_SIDES_MOBILE } },
        /* El ancho del contenido del pie es de su estilo, no de cada pie: nace
           limitado a 1280, y en Style › Secciones pasa a completo para todos. */
        contentWidth: '1280px',
        builtIn: true,
      },
    ],
    /*
     * Los tres tamaños de un icono nuevo: 20 px de símbolo y 8 de aire en
     * escritorio (caja de 36); 18 y 8 en tableta (34); 18 y 6 en el teléfono
     * (30). Eran 24 y 10 en todas partes —44 px—: demasiado para acompañar un
     * texto. Sólo cuenta para proyectos y templates NUEVOS: uno que ya guardó su
     * tema conserva lo suyo (ver `builtInIcons`).
     */
    iconComponents: [{
      id: 'icon-primary',
      nameKey: 'brand.iconPrimary',
      styles: {
        ...iconTokenStyles(theme.roles.icon),
        color: 'var(--wb-primary)',
        background: 'var(--wb-backgroundSecondary)',
        backgroundColor: 'var(--wb-backgroundSecondary)',
        borderWidth: '0px',
        borderStyle: 'none',
      },
      iconSize: 20,
      breakpoints: { tablet: { padding: '8px' }, mobile: { padding: '6px' } },
      breakpointSizes: { tablet: 18, mobile: 18 },
      builtIn: true,
    }, {
      id: 'icon-plain',
      nameKey: 'brand.iconPlain',
      styles: {
        color: 'var(--wb-primary)',
        background: 'transparent',
        backgroundColor: 'transparent',
        padding: '0px',
        borderRadius: '0px',
        borderWidth: '0px',
        borderStyle: 'none',
        boxShadow: 'none',
      },
      iconSize: 20,
      breakpointSizes: { tablet: 18, mobile: 18 },
      builtIn: true,
    }, {
      id: 'icon-secondary',
      nameKey: 'brand.iconSoft',
      styles: {
        color: 'var(--wb-muted)',
        padding: '6px',
        borderRadius: '12px',
      },
      iconSize: 20,
      breakpoints: { tablet: { padding: '6px' }, mobile: { padding: '4px' } },
      breakpointSizes: { tablet: 18, mobile: 18 },
      builtIn: true,
    }, {
      id: 'icon-accent',
      nameKey: 'brand.iconAccent',
      /* Caja del acento y símbolo del fondo (2026-10-08, pedido suyo): el
         icono destacado se lee como una pastilla del color de la marca. Era el
         acento sobre el primario, y el primario nace del acento: casi el mismo
         color en la mitad de las paletas. */
      styles: {
        color: 'var(--wb-background)',
        background: 'var(--wb-accent)',
        padding: '8px',
        borderRadius: '999px',
      },
      iconSize: 20,
      breakpoints: { tablet: { padding: '8px' }, mobile: { padding: '6px' } },
      breakpointSizes: { tablet: 18, mobile: 18 },
      builtIn: true,
    }],
  }
}

function syncThemeFromComponents(theme: ProjectTheme): ProjectTheme {
  const colors = { ...theme.colors }
  for (const token of theme.colorComponents) {
    if (token.id in colors) {
      colors[token.id as keyof PageTheme['colors']] = token.value
    }
  }

  const roles = { ...theme.roles }
  const roleBreakpoints: ProjectTheme['roleBreakpoints'] = {
    ...(theme.roleBreakpoints ?? {}),
    tablet: { ...(theme.roleBreakpoints?.tablet ?? {}) },
    mobile: { ...(theme.roleBreakpoints?.mobile ?? {}) },
  }

  for (const item of theme.typographyComponents) {
    if (!item.role) continue
    roles[item.role] = { ...roles[item.role], ...item.styles }
    if (item.breakpoints?.tablet) {
      roleBreakpoints.tablet = { ...(roleBreakpoints.tablet ?? {}), [item.role]: { ...item.breakpoints.tablet } }
    }
    if (item.breakpoints?.mobile) {
      roleBreakpoints.mobile = { ...(roleBreakpoints.mobile ?? {}), [item.role]: { ...item.breakpoints.mobile } }
    }
  }

  for (const item of theme.buttonComponents ?? []) {
    if (!item.role) continue
    roles[item.role] = { ...roles[item.role], ...item.styles }
    if (item.breakpoints?.tablet) {
      roleBreakpoints.tablet = { ...(roleBreakpoints.tablet ?? {}), [item.role]: { ...item.breakpoints.tablet } }
    }
    if (item.breakpoints?.mobile) {
      roleBreakpoints.mobile = { ...(roleBreakpoints.mobile ?? {}), [item.role]: { ...item.breakpoints.mobile } }
    }
  }

  const defaultCard = theme.cardComponents?.find((item) => item.id === 'card-default')
  if (defaultCard) {
    roles.card = { ...roles.card, ...cardContainerStyles(defaultCard.styles) }
    if (defaultCard.breakpoints?.tablet) {
      roleBreakpoints.tablet = { ...(roleBreakpoints.tablet ?? {}), card: { ...defaultCard.breakpoints.tablet } }
    }
    if (defaultCard.breakpoints?.mobile) {
      roleBreakpoints.mobile = { ...(roleBreakpoints.mobile ?? {}), card: { ...defaultCard.breakpoints.mobile } }
    }
  }

  const defaultSection = theme.sectionComponents?.find((item) => item.id === 'section-default')
  if (defaultSection) {
    roles.section = { ...roles.section, ...sectionContainerStyles(defaultSection.styles) }
    if (defaultSection.breakpoints?.tablet) {
      roleBreakpoints.tablet = { ...(roleBreakpoints.tablet ?? {}), section: { ...defaultSection.breakpoints.tablet } }
    }
    if (defaultSection.breakpoints?.mobile) {
      roleBreakpoints.mobile = { ...(roleBreakpoints.mobile ?? {}), section: { ...defaultSection.breakpoints.mobile } }
    }
  }

  return { ...theme, colors, roles, roleBreakpoints }
}

/**
 * El gris automático que llevaba el párrafo, en sus dos versiones: el del tema
 * por defecto y el que ponía aplicar una paleta. Nadie lo eligió —no era un
 * color de la paleta ni se podía elegir en ningún sitio—, así que al abrir un
 * proyecto pasa a la tinta. Sólo esas dos cadenas exactas: cualquier otro color
 * es una decisión y se respeta.
 */
const AUTO_PARAGRAPH_GRAYS = new Set([
  'color-mix(in srgb, var(--wb-text) 68%, transparent)',
  'color-mix(in srgb, var(--wb-text) 74%, var(--wb-background))',
])

function withoutAutoParagraphGray(color: string | undefined): string | undefined {
  return typeof color === 'string' && AUTO_PARAGRAPH_GRAYS.has(color.trim()) ? 'var(--wb-text)' : color
}

function withParagraphInk<T extends Record<string, string | undefined>>(styles: T): T {
  const color = withoutAutoParagraphGray(styles.color)
  return color === styles.color ? styles : { ...styles, color }
}

/** Actualiza la tinta de fábrica anterior sin sustituir colores elegidos a mano. */
function migrateSecondaryTextInk(role: ThemeRole | undefined, incoming: Partial<StyleProperties> | undefined, styles: StyleProperties, colors: PageTheme['colors']): StyleProperties {
  if (incoming?.opacity !== undefined) return styles
  const legacy = role === 'eyebrow'
    ? ['var(--wb-primary)', 'var(--wb-accent)', colors.primary, colors.accent]
    : role === 'smallText' ? ['var(--wb-muted)', colors.muted] : []
  return styles.color && legacy.includes(styles.color) ? { ...styles, color: 'var(--wb-text)' } : styles
}

/**
 * La tarjeta con borde, si quedó igual que la de base.
 *
 * Los temas a los que se les aplicó un estilo de diseño o un look antes de que
 * la tarjeta con borde tuviera identidad propia la guardaron idéntica a la de
 * base —mismo relleno, sin borde, sin sombra—, y aplicarla no cambiaba nada.
 * Sólo en ese caso pasa a la de contorno; si alguien la personalizó, ya no es
 * igual y se respeta.
 */
function withDistinctSoftCard(cards: GlobalCardComponent[]): GlobalCardComponent[] {
  const base = cards.find((card) => card.id === 'card-default')?.styles
  const soft = cards.find((card) => card.id === 'card-soft')
  if (!base || !soft?.styles) return cards
  const same = (['background', 'borderWidth', 'borderColor', 'boxShadow'] as const)
    .every((key) => String(soft.styles?.[key] ?? '') === String(base[key] ?? ''))
  if (!same) return cards
  return cards.map((card) => (card === soft ? { ...card, styles: outlinedCardStyles(card.styles ?? {}) } : card))
}

export function normalizeProjectTheme(raw: unknown, fallbackTheme: PageTheme = DEFAULT_PAGE_THEME): ProjectTheme {
  const base = normalizeTheme(raw && typeof raw === 'object' ? raw : fallbackTheme)
  const input = (raw && typeof raw === 'object' ? raw : {}) as Partial<ProjectTheme>
  const baseline = input.styleCombinationBaseline
  const validBaseline = baseline
    && typeof baseline.id === 'string'
    && baseline.colors && typeof baseline.colors === 'object'
    && baseline.roles && typeof baseline.roles === 'object'
    && Array.isArray(baseline.colorComponents)
    && Array.isArray(baseline.typographyComponents)
    && Array.isArray(baseline.buttonComponents)
    && Array.isArray(baseline.cardComponents)
    && Array.isArray(baseline.sectionComponents)
    && Array.isArray(baseline.iconComponents)
      ? baseline
      : undefined
  const defaults = componentsFromTheme(base)
  const builtInColors = defaults.colorComponents.map((token) => {
    const incoming = input.colorComponents?.find((item) => item.id === token.id)
    /* `nameKey` se repone DESPUÉS de lo guardado a propósito. Los proyectos
       creados antes de esto llevan «Primario» escrito en su tema, y como los de
       fábrica no se pueden renombrar, ese texto no es una decisión de nadie: es
       el nombre de cuando se creó el proyecto. Dejarlo ganar mantendría el
       español en los proyectos viejos para siempre. */
    const fusionado = {
      ...token,
      ...(incoming ?? {}),
      value: token.value,
      builtIn: true,
      cssVar: token.cssVar,
      ...(token.nameKey ? { nameKey: token.nameKey } : {}),
    }
    /* Y se tira el nombre que trajera guardado. Los proyectos de antes llevan
       «Primario» o «Primary» dentro según por dónde se crearan; como los de
       fábrica no se pueden renombrar, ese texto no lo eligió nadie. Dejarlo
       mantendría un idioma congelado dentro del dato.
       Se borra con `delete` y no poniéndolo a `undefined` porque el tipado de
       este repo distingue «sin propiedad» de «propiedad que vale undefined». */
    if (fusionado.nameKey) delete (fusionado as { name?: string }).name
    return fusionado
  })
  const customColors = (input.colorComponents ?? [])
    .filter((token) => !defaults.colorComponents.some((item) => item.id === token.id))
    .map((token) => ({
      id: token.id,
      name: token.name || 'Color',
      value: token.value || '#000000',
      cssVar: token.cssVar || `--wb-${token.id}`,
      builtIn: false,
    }))

  const builtInTypography = defaults.typographyComponents.map((item) => {
    const incoming = input.typographyComponents?.find((style) => style.id === item.id)
    const next: GlobalTypographyComponent = {
      id: item.id,
      /* El de fábrica manda, y si no lo lleva no se pone: guardar un nombre en
         un componente de fábrica es lo que congelaba el idioma del proyecto. */
      ...(item.name ? { name: item.name } : {}),
      ...(item.nameKey ? { nameKey: item.nameKey } : {}),
      styles: migrateSecondaryTextInk(item.role, incoming?.styles, withParagraphInk({ ...item.styles, ...(incoming?.styles ?? {}) }), base.colors),
      breakpoints: { ...(item.breakpoints ?? {}), ...(incoming?.breakpoints ?? {}) },
      builtIn: true,
    }
    if (item.role !== undefined) next.role = item.role
    const tag = incoming?.tag ?? item.tag
    if (tag !== undefined) next.tag = tag
    return next
  })
  const customTypography = (input.typographyComponents ?? [])
    .filter((style) => !defaults.typographyComponents.some((item) => item.id === style.id))
    .map((style) => {
      const next: GlobalTypographyComponent = {
        id: style.id,
        name: style.name || 'Texto',
        styles: withParagraphInk({ ...(style.styles ?? {}) }),
        builtIn: false,
      }
      if (style.tag !== undefined) next.tag = style.tag
      if (style.role !== undefined) next.role = style.role
      if (style.breakpoints !== undefined) next.breakpoints = style.breakpoints
      return next
    })

  const builtInButtons: GlobalButtonComponent[] = defaults.buttonComponents.map((item) => {
    const incoming = input.buttonComponents?.find((style) => style.id === item.id)
    const incomingStyles = incoming?.styles
      ? (() => {
          const styles = { ...incoming.styles }
          // Older primary-button defaults used the `border: none` shorthand.
          // Remove it so the explicit zero width and the linked colour remain
          // independently editable in Marca > Botones.
          if (item.id === 'button-primary' && styles.border === 'none') {
            delete styles.border
            styles.borderWidth = '0px'
            styles.borderStyle = 'solid'
            styles.borderColor ||= item.styles.borderColor ?? 'var(--wb-muted)'
          }
          return {
          ...styles,
          /* Aquí un primario de botón `var(--wb-primary)` se pasaba a `muted`. Ya
             no: el botón es el color de marca, el primario (ver `retargetButton`
             en `presets`). */
          /* Aquí también se pasaba el texto `var(--wb-text)` y el borde
             `var(--wb-border)` del botón secundario a `muted`: era para llevar
             los proyectos viejos al aspecto nuevo, pero corría en CADA guardado
             y deshacía la elección de quien ponía a mano «Texto» o «Borde» en
             Marca › Botones (sólo le dejaba «Secundario»). Lo que se elige se
             respeta. */
          }
        })()
      : undefined
    const next: GlobalButtonComponent = {
      id: item.id,
      /* El de fábrica manda, y si no lo lleva no se pone: guardar un nombre en
         un componente de fábrica es lo que congelaba el idioma del proyecto. */
      ...(item.name ? { name: item.name } : {}),
      ...(item.nameKey ? { nameKey: item.nameKey } : {}),
      styles: { ...item.styles, ...(incomingStyles ?? {}) },
      builtIn: true,
    }
    if (item.role !== undefined) next.role = item.role
    const breakpoints = { ...(item.breakpoints ?? {}), ...(incoming?.breakpoints ?? {}) }
    if (Object.keys(breakpoints).length > 0) next.breakpoints = breakpoints
    /* Los estados y la transición del estilo: sin copiarlos aquí se perdían al
       guardar. Ver `interactionStates`. */
    if (incoming?.states !== undefined) next.states = incoming.states
    if (incoming?.transition !== undefined) next.transition = incoming.transition
    return next
  })
  const customButtons = (input.buttonComponents ?? [])
    .filter((style) => !defaults.buttonComponents.some((item) => item.id === style.id))
    .map((style) => {
      const next: GlobalButtonComponent = {
        id: style.id,
        name: style.name || 'Botón',
        styles: { ...(style.styles ?? {}) },
        builtIn: false,
      }
      if (style.role !== undefined) next.role = style.role
      if (style.breakpoints !== undefined) next.breakpoints = style.breakpoints
      if (style.states !== undefined) next.states = style.states
      if (style.transition !== undefined) next.transition = style.transition
      return next
    })

  const builtInCards: GlobalCardComponent[] = defaults.cardComponents.map((item) => {
    const incoming = input.cardComponents?.find((style) => style.id === item.id)
    const isRenamedColorCard = item.id === 'card-elevated' && incoming?.name === 'Tarjeta elevada'
    const hasLegacyElevatedDefaults = isRenamedColorCard
      && (incoming?.styles?.background === 'var(--wb-surface)' || incoming?.styles?.backgroundColor === 'var(--wb-surface)')
      && incoming?.styles?.boxShadow === '0 14px 34px rgba(15,23,42,0.14)'
    const hasLegacySoftBackground = item.id === 'card-soft'
      && incoming?.styles?.background === 'var(--wb-backgroundSecondary)'
      && incoming?.styles?.backgroundColor === 'var(--wb-backgroundSecondary)'
    const hasLegacySoftEffects = item.id === 'card-soft'
      && incoming?.styles?.borderColor === 'var(--wb-border)'
      && incoming?.styles?.boxShadow === '0 4px 16px rgba(15,23,42,0.08)'
    const incomingWithMigratedRadius = incoming?.styles
      ? {
          ...incoming.styles,
          ...(
            (item.id === 'card-soft' && incoming.styles.borderRadius === '20px')
            || (item.id === 'card-elevated' && incoming.styles.borderRadius === '12px')
              ? { borderRadius: '24px' }
              : {}
          ),
        }
      : undefined
    const incomingStyles = hasLegacyElevatedDefaults
      ? {
          ...(incomingWithMigratedRadius ?? {}),
          background: 'var(--wb-accent)',
          backgroundColor: 'var(--wb-accent)',
          borderColor: 'var(--wb-accent)',
          boxShadow: 'none',
        }
      : hasLegacySoftBackground
        ? {
            ...(incomingWithMigratedRadius ?? {}),
            background: 'var(--wb-card)',
            backgroundColor: 'var(--wb-card)',
            ...(hasLegacySoftEffects
              ? {
                  borderColor: 'color-mix(in srgb, var(--wb-border) 35%, transparent)',
                  boxShadow: '0 6px 16px rgba(15,23,42,0.05)',
                }
              : {}),
          }
      : hasLegacySoftEffects
        ? {
            ...(incomingWithMigratedRadius ?? {}),
            borderColor: 'color-mix(in srgb, var(--wb-border) 35%, transparent)',
            boxShadow: '0 6px 16px rgba(15,23,42,0.05)',
          }
      : item.id === 'card-default' && incomingWithMigratedRadius
        ? migrateBaseCardDefaults(incomingWithMigratedRadius)
        : incomingWithMigratedRadius
    const next: GlobalCardComponent = {
      id: item.id,
      /* Sin nombre: los de fábrica lo dicen con su clave. `isRenamedColorCard`
         sigue mirando el nombre de ENTRADA —es el detector de proyectos viejos
         guardados como «Tarjeta elevada»— y por eso quitarlo de la salida no lo
         rompe: lo que se lee y lo que se escribe no son el mismo objeto. */
      ...(item.nameKey ? { nameKey: item.nameKey } : {}),
      styles: cardContainerStyles({ ...item.styles, ...(incomingStyles ?? {}) }),
      builtIn: true,
    }
    if (item.role !== undefined) next.role = item.role
    const breakpoints = { ...(item.breakpoints ?? {}), ...(incoming?.breakpoints ?? {}) }
    if (Object.keys(breakpoints).length > 0) next.breakpoints = breakpoints
    /* Los estados y la transición del estilo: sin copiarlos aquí se perdían al
       guardar. Ver `interactionStates`. */
    if (incoming?.states !== undefined) next.states = incoming.states
    if (incoming?.transition !== undefined) next.transition = incoming.transition
    return next
  })
  const customCards: GlobalCardComponent[] = (input.cardComponents ?? [])
    .filter((style) => !defaults.cardComponents.some((item) => item.id === style.id))
    .map((style) => {
      const next: GlobalCardComponent = {
        id: style.id,
        name: style.name || 'Tarjeta',
        styles: cardContainerStyles(style.styles ?? {}),
        builtIn: false,
      }
      if (style.breakpoints !== undefined) next.breakpoints = style.breakpoints
      if (style.states !== undefined) next.states = style.states
      if (style.transition !== undefined) next.transition = style.transition
      return next
    })

  /* Los estilos de la barra y del footer se guardan enteros: de fábrica sólo
     cuando el proyecto aún no los tiene. Mezclados con los de fábrica, lo que
     alguien borraba —la línea de debajo de la barra, un relleno— volvía al
     cargar con su valor de fábrica, y parecía que el popup no hacía nada. */
  const WHOLE_SECTION_STYLES = new Set(['section-nav', 'section-footer'])
  const NEW_ONLY_CONTENT_WIDTH = new Set(['section-default', 'section-secondary', 'section-accent', 'section-nav'])
  const builtInSections: GlobalSectionComponent[] = defaults.sectionComponents.map((item) => {
    const incoming = input.sectionComponents?.find((style) => style.id === item.id)
    const whole = WHOLE_SECTION_STYLES.has(item.id) && incoming?.styles
    /* Los guardados antes de que llevaran aire a los lados lo reciben; uno al
       que alguien ya le puso relleno —o se lo quitó a 0— se queda como está. */
    const wholeStyles = whole
      ? (incoming.styles.padding === undefined && incoming.styles.paddingLeft === undefined && incoming.styles.paddingRight === undefined
        ? { ...incoming.styles, ...SECTION_SIDES }
        : { ...incoming.styles })
      : undefined
    const next: GlobalSectionComponent = {
      id: item.id,
      ...(item.nameKey ? { nameKey: item.nameKey } : {}),
      styles: sectionContainerStyles(wholeStyles ?? { ...item.styles, ...(incoming?.styles ?? {}) }),
      builtIn: true,
    }
    if (item.role !== undefined) next.role = item.role
    /* Estos tres nacen limitados sólo si el proyecto aún no tiene el estilo: en
       uno que ya lo guardó, sin ancho quiere decir «Auto» —cada sección con el
       suyo— y así se queda. Si no, todas las barras a todo el ancho de los
       sitios publicados pasarían a 1280 al volver a publicar. El pie siempre
       tuvo su 1280 de fábrica y lo conserva igual que antes. */
    const fallbackWidth = NEW_ONLY_CONTENT_WIDTH.has(item.id) && incoming ? undefined : item.contentWidth
    const contentWidth = normalizeContentWidth(incoming?.contentWidth) ?? fallbackWidth
    if (contentWidth) next.contentWidth = contentWidth
    const contentWidthBreakpoints = normalizeContentWidthBreakpoints(incoming?.contentWidthBreakpoints)
    if (contentWidthBreakpoints) next.contentWidthBreakpoints = contentWidthBreakpoints
    /* Por tamaño y no entero: un proyecto que guardó su móvil con sólo los
       lados conserva el aire de arriba y abajo de fábrica. */
    const breakpoints: Record<string, StyleProperties> = whole ? { ...(incoming?.breakpoints ?? {}) } : { ...(item.breakpoints ?? {}) }
    if (!whole) {
      for (const [bp, styles] of Object.entries(incoming?.breakpoints ?? {})) {
        breakpoints[bp] = { ...(breakpoints[bp] ?? {}), ...(styles ?? {}) }
      }
    }
    if (Object.keys(breakpoints).length > 0) next.breakpoints = breakpoints
    return next
  })
  const customSections: GlobalSectionComponent[] = (input.sectionComponents ?? [])
    .filter((style) => !defaults.sectionComponents.some((item) => item.id === style.id))
    .map((style) => {
      const next: GlobalSectionComponent = {
        id: style.id,
        name: style.name || 'Sección',
        styles: sectionContainerStyles(style.styles ?? {}),
        builtIn: false,
      }
      const contentWidth = normalizeContentWidth(style.contentWidth)
      if (contentWidth) next.contentWidth = contentWidth
      const contentWidthBreakpoints = normalizeContentWidthBreakpoints(style.contentWidthBreakpoints)
      if (contentWidthBreakpoints) next.contentWidthBreakpoints = contentWidthBreakpoints
      if (style.breakpoints !== undefined) next.breakpoints = style.breakpoints
      return next
    })

  const builtInIcons: GlobalIconComponent[] = defaults.iconComponents.map((item) => {
    const incoming = input.iconComponents?.find((icon) => icon.id === item.id)
    const mergedStyles = { ...item.styles, ...(incoming?.styles ?? {}) }
    // Keep untouched legacy defaults linked to their semantic palette tokens.
    // Literal/custom colors remain unchanged.
    if (item.id === 'icon-secondary' && incoming?.styles?.color === 'var(--wb-primary)') {
      mergedStyles.color = 'var(--wb-muted)'
    }
    /* El «de acento» de antes sin tocar —acento sobre primario— pasa al de
       ahora: caja del acento, símbolo del fondo. Uno con colores elegidos a mano
       se queda como está. */
    const incomingFill = incoming?.styles?.background ?? incoming?.styles?.backgroundColor
    if (item.id === 'icon-accent' && incoming?.styles?.color === 'var(--wb-accent)' && (incomingFill === undefined || incomingFill === 'var(--wb-primary)')) {
      mergedStyles.color = 'var(--wb-background)'
      mergedStyles.background = 'var(--wb-accent)'
      if (mergedStyles.backgroundColor !== undefined) mergedStyles.backgroundColor = 'var(--wb-accent)'
    }
    if (item.id === 'icon-primary' && !incoming?.styles?.borderWidth) {
      mergedStyles.borderWidth = '0px'
      mergedStyles.borderStyle = 'none'
    }
    // The former soft-icon default included a secondary fill. Remove that exact
    // untouched legacy value while preserving any genuinely customized color.
    if (
      item.id === 'icon-secondary'
      && mergedStyles.background === 'var(--wb-backgroundSecondary)'
    ) {
      delete mergedStyles.background
    }
    if (
      item.id === 'icon-secondary'
      && mergedStyles.backgroundColor === 'var(--wb-backgroundSecondary)'
    ) {
      delete mergedStyles.backgroundColor
    }
    readableIconInk(item.id, mergedStyles, base.colors)
    return {
      id: item.id,
      ...(item.nameKey ? { nameKey: item.nameKey } : {}),
      styles: iconTokenStyles(mergedStyles),
      iconSize: typeof incoming?.iconSize === 'number'
        && !((item.id === 'icon-secondary' && incoming.iconSize === 20) || (item.id === 'icon-accent' && incoming.iconSize === 22))
        ? incoming.iconSize
        : item.iconSize,
      /* Los de tableta y teléfono de fábrica sólo para un tema NUEVO. Uno que ya
         guardó su icono —con o sin ellos— se queda como está: si no, cada
         proyecto viejo cambiaría de tamaño en el móvil al abrirlo. */
      ...((incoming ? incoming.breakpoints : item.breakpoints) ? { breakpoints: (incoming ? incoming.breakpoints : item.breakpoints)! } : {}),
      ...((incoming ? incoming.breakpointSizes : item.breakpointSizes) ? { breakpointSizes: (incoming ? incoming.breakpointSizes : item.breakpointSizes)! } : {}),
      builtIn: true,
    }
  })
  const customIcons: GlobalIconComponent[] = (input.iconComponents ?? [])
    .filter((icon) => !defaults.iconComponents.some((item) => item.id === icon.id))
    .map((icon) => ({
      id: icon.id,
      name: icon.name || 'Icono',
      styles: iconTokenStyles(icon.styles ?? {}),
      iconSize: typeof icon.iconSize === 'number' ? icon.iconSize : 24,
      ...(icon.breakpoints ? { breakpoints: icon.breakpoints } : {}),
      ...(icon.breakpointSizes ? { breakpointSizes: icon.breakpointSizes } : {}),
      builtIn: false,
    }))

  const incomingLogo = (input.brandLogo ?? {}) as Partial<BrandLogo>
  const incomingImageSize = Number.parseFloat(incomingLogo.imageSize ?? '')
  const brandLogo: BrandLogo = {
    ...DEFAULT_BRAND_LOGO,
    ...incomingLogo,
    nameColor: !incomingLogo.nameColor || incomingLogo.nameColor === 'var(--wb-background)'
      ? DEFAULT_BRAND_LOGO.nameColor
      : incomingLogo.nameColor,
    nameColorDark: incomingLogo.nameColorDark || DEFAULT_BRAND_LOGO.nameColorDark,
    /* El color del fondo de la página sólo se corrige sin placa: ahí el
       símbolo desaparecía contra la página. Con placa es justo lo que se busca
       —un símbolo claro sobre la placa de marca— y corregirlo siempre dejaba
       el icono en negro eligiera lo que eligiera quien edita. */
    iconColor: !incomingLogo.iconColor
      || (incomingLogo.iconColor === 'var(--wb-background)' && !(incomingLogo.iconBackground ?? DEFAULT_BRAND_LOGO.iconBackground))
      ? DEFAULT_BRAND_LOGO.iconColor
      : incomingLogo.iconColor,
    iconColorDark: incomingLogo.iconColorDark || DEFAULT_BRAND_LOGO.iconColorDark,
    iconBackgroundDark: typeof incomingLogo.iconBackgroundDark === 'string'
      ? incomingLogo.iconBackgroundDark
      : (incomingLogo.iconBackground ?? DEFAULT_BRAND_LOGO.iconBackgroundDark),
    // A stored value of the wrong shape must not reach the renderer.
    mark: ['none', 'icon', 'image'].includes(incomingLogo.mark as string)
      ? incomingLogo.mark as BrandLogo['mark']
      : DEFAULT_BRAND_LOGO.mark,
    iconSize: typeof incomingLogo.iconSize === 'string' && incomingLogo.iconSize.trim()
      ? incomingLogo.iconSize
      : DEFAULT_BRAND_LOGO.iconSize,
    imageSize: Number.isFinite(incomingImageSize) && incomingImageSize !== 36
      ? `${Math.min(320, Math.max(6, incomingImageSize))}px`
      : DEFAULT_BRAND_LOGO.imageSize,
    showName: typeof incomingLogo.showName === 'boolean' ? incomingLogo.showName : true,
    iconBackgroundOpacity: typeof incomingLogo.iconBackgroundOpacity === 'number'
      && Number.isFinite(incomingLogo.iconBackgroundOpacity)
      ? Math.min(100, Math.max(0, incomingLogo.iconBackgroundOpacity))
      : 100,
  }

  return syncThemeFromComponents({
    ...base,
    ...(validBaseline ? { styleCombinationBaseline: validBaseline } : {}),
    ...((input as { cardSurfaceAuto?: unknown }).cardSurfaceAuto === false ? { cardSurfaceAuto: false as const } : {}),
    ...((input as { sectionToneAuto?: unknown }).sectionToneAuto === true ? { sectionToneAuto: true as const } : {}),
    ...(normalizeSiteMotion((input as { motion?: unknown }).motion) ? { motion: normalizeSiteMotion((input as { motion?: unknown }).motion)! } : {}),
    brandLogo,
    colorComponents: [...builtInColors, ...customColors],
    typographyComponents: [...builtInTypography, ...customTypography],
    buttonComponents: [...builtInButtons, ...customButtons],
    cardComponents: withDistinctSoftCard([...builtInCards, ...customCards]),
    sectionComponents: [...builtInSections, ...customSections],
    iconComponents: [...builtInIcons, ...customIcons],
  })
}

const LEGACY_ROLE_COLOR_MIGRATION: Partial<Record<ThemeRole, Record<string, [string, string]>>> = {
  section:    { background: ['#f8fafc', 'var(--wb-background)'], color: ['#0f172a', 'var(--wb-text)'] },
  card:       { background: ['#ffffff', 'var(--wb-card)'], color: ['#0f172a', 'var(--wb-text)'], borderColor: ['#cbd3e6', 'var(--wb-border)'] },
  stat:       { background: ['#ffffff', 'var(--wb-card)'], color: ['#0f172a', 'var(--wb-text)'], borderColor: ['#e2e8f0', 'var(--wb-border)'] },
  badge:      { background: ['#ffffff', 'var(--wb-surface)'] },
  icon:       { color: ['#2563eb', 'var(--wb-primary)'] }, // accent-ok: valor por defecto de un elemento del sitio
  rating:     { color: ['#f59e0b', 'var(--wb-accent)'] },
  navigation: { background: ['#ffffff', 'var(--wb-surface)'], color: ['#0f172a', 'var(--wb-text)'], borderColor: ['#e2e8f0', 'var(--wb-border)'] },
}

export function normalizeTheme(raw: unknown): PageTheme {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Partial<PageTheme>
  const inputColors: Partial<PageTheme['colors']> = input.colors ?? {}
  const legacySurface = inputColors.surface ?? DEFAULT_PAGE_THEME.colors.surface
  const colors = {
    ...DEFAULT_PAGE_THEME.colors,
    ...inputColors,
    backgroundSecondary: inputColors.backgroundSecondary ?? legacySurface,
    card: inputColors.card ?? legacySurface,
  }
  /* También para temas guardados antes de esta regla: al abrirlos, primario y
     acento recuperan sus dos papeles sin exigir que se reaplique una paleta. */
  colors.primary = distinctPrimaryColor(colors.primary, colors.accent)
  const incomingRoles: Partial<PageTheme['roles']> = input.roles ?? {}
  const roles = (Object.keys(DEFAULT_PAGE_THEME.roles) as ThemeRole[]).reduce((acc, role) => {
    const merged: Record<string, string> = {
      ...DEFAULT_PAGE_THEME.roles[role],
      ...(incomingRoles[role] ?? {}),
    }
    if (role === 'card') Object.assign(merged, migrateBaseCardDefaults(merged as StyleProperties))
    if (role === 'paragraph' && merged.color) merged.color = withoutAutoParagraphGray(merged.color) ?? merged.color
    Object.assign(merged, migrateSecondaryTextInk(role, incomingRoles[role], merged as StyleProperties, colors))
    const migration = LEGACY_ROLE_COLOR_MIGRATION[role]
    if (migration) {
      for (const [prop, [oldHex, newVar]] of Object.entries(migration)) {
        if (typeof merged[prop] === 'string' && merged[prop].toLowerCase() === oldHex) {
          merged[prop] = newVar
        }
      }
    }
    acc[role] = merged as StyleProperties
    return acc
  }, {} as Record<ThemeRole, StyleProperties>)
  // Al añadir texto pequeño a un tema anterior, usa la fuente de su cuerpo.
  if (!incomingRoles.smallText?.fontFamily && roles.paragraph.fontFamily) roles.smallText.fontFamily = roles.paragraph.fontFamily
  const typography = {
    ...DEFAULT_PAGE_THEME.typography,
    ...(input.typography ?? {}),
  }
  const incomingRoleProps: Partial<Record<ThemeRole, ThemeRoleElementProps>> = input.roleProps ?? {}
  const roleProps = (Object.keys(DEFAULT_PAGE_THEME.roles) as ThemeRole[]).reduce((acc, role) => {
    const defaults = DEFAULT_PAGE_THEME.roleProps?.[role]
    const incoming = incomingRoleProps[role]
    if (defaults || incoming) acc[role] = { ...(defaults ?? {}), ...(incoming ?? {}) }
    return acc
  }, {} as Partial<Record<ThemeRole, ThemeRoleElementProps>>)
  const roleBreakpoints = {
    tablet: { ...(input.roleBreakpoints?.tablet ?? {}) },
    mobile: { ...(input.roleBreakpoints?.mobile ?? {}) },
  }
  return {
    presetId: input.presetId ?? DEFAULT_PAGE_THEME.presetId ?? 'saas-clean',
    typography,
    colors,
    roles,
    roleProps,
    roleBreakpoints,
  }
}
